import {
  ellipsizeMiddle,
  formatHumanDate,
  formatToMaxFourDigits,
  IconWrapper,
} from '@flexo/general';
import { navigateToMember, navigateToSite } from '@flexo/resolvers';
import CircularProgress from '@mui/material/CircularProgress';
import {
  MutableRefObject,
  ReactNode,
  Ref,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import styles from '../table.module.scss';
import { openPdfWithTitle } from './helpers-types';

const assignRef = <T,>(target: Ref<T> | undefined, value: T | null) => {
  if (!target) return;

  if (typeof target === 'function') {
    target(value);
    return;
  }

  (target as MutableRefObject<T | null>).current = value;
};

/**
 * Factory function that creates a getCellContent function for PaymentsTable
 * @param t - Translation function
 * @param isNavigable - Whether site names should be clickable
 * @param memberOrMembers - Optional single member or members array for site payments table.
 *   When an array is provided, the correct member is resolved per-row via invoice userId.
 * @param onDownloadInvoice - Optional callback for downloading invoices on-demand
 * @param loadingInvoiceNo - Optional invoice number currently being downloaded
 * @returns A function to generate cell content based on column type and site data
 */
export function createGetCellContent(
  t: any,
  isNavigable = true,
  memberOrMembers?: any,
  onDownloadInvoice?: (site: any) => void,
  loadingInvoiceNo?: string | null
) {
  return function getCellContent(column: any, site: any): ReactNode {
    switch (column.field) {
      case 'resident': {
        // Resolve the correct member for this specific invoice row
        let member: any = null;
        if (Array.isArray(memberOrMembers)) {
          // Look up member by invoice userId first, then fall back to site memberId
          member = memberOrMembers.find(
            (m: any) => m.memberId === site?.userId
          ) || memberOrMembers.find(
            (m: any) => m.memberId === site?.memberId
          );
        } else if (memberOrMembers) {
          member = memberOrMembers;
        }

        if (member) {
          return (
            <>
              <div
                className={styles.Table__Payments__Cell__MemberName}
                onClick={() => navigateToMember('member', member.memberId)}
              >
                {member?.memberFirstName || ''} {member?.memberLastName || ''}
              </div>
              <div className={styles.Table__Payments__Cell__MemberID}>
                {member.memberExternalId}
              </div>
            </>
          );
        }
        return null;
      }

      case 'site':
        if (isNavigable) {
          return (
            <>
              <div
                className={styles.Table__Payments__Cell__SiteName}
                onClick={() => navigateToSite('site', site.siteId)}
              >
                {site.siteName}
              </div>
              <div className={styles.Table__Payments__Cell__Pod}>
                {ellipsizeMiddle(site.pod)}
              </div>
            </>
          );
        } else {
          return (
            <>
              <div className={styles.Table__Payments__Cell__SiteName}>
                {site.siteName}
              </div>
              <div className={styles.Table__Payments__Cell__Pod}>
                {ellipsizeMiddle(site.pod)}
              </div>
            </>
          );
        }

      case 'invoice': {
        const invoiceId = site?.invoice_no || site?.invoiceID;
        const isLoading = loadingInvoiceNo === invoiceId;

        const handleClick = (e: React.MouseEvent) => {
          e.preventDefault();
          // If file_base64 is already available (legacy), use it directly
          if (site?.file_base64) {
            openPdfWithTitle(
              site.file_base64,
              site?.invoiceID || site?.fileName || 'Document'
            );
          } else if (onDownloadInvoice) {
            // Otherwise, fetch on-demand
            onDownloadInvoice(site);
          }
        };

        return (
          <>
            <div className={styles.Table__Payments__Cell__Type__FirstColumn}>
              {site?.url ? (
                <a
                  href="#"
                  onClick={handleClick}
                  style={{
                    pointerEvents: isLoading ? 'none' : 'auto',
                    opacity: isLoading ? 0.6 : 1,
                  }}
                >
                  {site?.base64Image}
                  {site.invoice_type &&
                    t(
                      `widgets.tables.paymentsTable.invoiceType.${site.invoice_type}`
                    )}
                  {site.invoiceID}
                  {isLoading ? (
                    <CircularProgress size={14}
                    color="inherit"
                    sx={{
                      marginLeft: '4px',
                      marginTop: '-4px',
                      transformOrigin: 'center',
                      animation: 'none'
                    }} />
                  ) : (
                    <IconWrapper iconName="download" />
                  )}
                </a>
              ) : (
                <>
                  {site.invoice_type &&
                    t(
                      `widgets.tables.paymentsTable.invoiceType.${site.invoice_type}`
                    )}
                  {site.invoiceID}
                </>
              )}
            </div>
            <div className={styles.Table__Payments__Cell__Issued}>
              {t(`widgets.tables.paymentsTable.issued`)}:{' '}
              {formatHumanDate(site.invoiceDate)}
            </div>
          </>
        );
      }

      case 'amount':
        return (
          <div className={styles.Table__Payments__Cell__Amount}>
            {site.unit} {formatToMaxFourDigits(site.amount, true)}
          </div>
        );

      case 'paid':
        return (
          <>
            <div className={styles.Table__Payments__Cell__Paid}>
              {site.paid ? (
                <IconWrapper iconName="tick_-encircled_active_secondary" />
              ) : null}
              {site.paid
                ? t(`widgets.tables.paymentsTable.paid.true`)
                : t(`widgets.tables.paymentsTable.paid.false`)}
            </div>
            <div className={styles.Table__Payments__Cell__DueDate}>
              {t(`widgets.tables.paymentsTable.dueDate`)}:{' '}
              {formatHumanDate(site.invoiceDueDate)}
            </div>
          </>
        );

      default:
        return site[column.field];
    }
  };
}

// Smoothly animates between open/closed using measured height
export function Collapsible({
  open,
  children,
  className,
  outerRef,
}: {
  open: boolean;
  children: React.ReactNode;
  className?: string;
  outerRef?: Ref<HTMLDivElement>;
}) {
  const ref =
    useRef<HTMLDivElement | null>(null) as MutableRefObject<HTMLDivElement | null>;
  const innerRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);
  const closeTimeoutRef = useRef<number | null>(null);
  const openRafRef = useRef<number | null>(null);
  const closeRafRef = useRef<number | null>(null);
  const [shouldRender, setShouldRender] = useState(open);

  const syncOpenHeight = () => {
    const el = ref.current;
    const inner = innerRef.current;
    if (!el || !inner || !open || !shouldRender) return;

    const measuredHeight = inner.getBoundingClientRect().height;

    // If the collapse is logically open but got stuck at 0px while content was
    // still settling, force it back to auto as soon as real content appears.
    if (measuredHeight > 0 && el.style.height === '0px') {
      el.style.transition = 'none';
      el.style.height = 'auto';
    }
  };

  const clearScheduledWork = () => {
    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    if (openRafRef.current !== null) {
      window.cancelAnimationFrame(openRafRef.current);
      openRafRef.current = null;
    }
    if (closeRafRef.current !== null) {
      window.cancelAnimationFrame(closeRafRef.current);
      closeRafRef.current = null;
    }
  };

  useEffect(() => {
    if (open) {
      setShouldRender(true);
    }
  }, [open]);

  useEffect(() => () => {
    clearScheduledWork();
  }, []);

  useLayoutEffect(() => {
    if (!open || !shouldRender) return;

    syncOpenHeight();

    if (typeof ResizeObserver === 'undefined' || !innerRef.current) return;

    const observer = new ResizeObserver(() => {
      syncOpenHeight();
    });

    observer.observe(innerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [open, shouldRender, children]);

  useLayoutEffect(() => {
    const el = ref.current;
    const inner = innerRef.current;
    if (!el || !inner || !shouldRender) return;

    clearScheduledWork();

    const onEnd = (event: TransitionEvent) => {
      if (event.target !== el || event.propertyName !== 'height') return;

      if (open) {
        el.style.height = 'auto';
      } else {
        setShouldRender(false);
      }
    };

    const measuredHeight = `${inner.getBoundingClientRect().height}px`;

    if (isFirstRender.current) {
      el.style.transition = 'none';
      el.style.height = open ? 'auto' : '0px';
      isFirstRender.current = false;
      return;
    }

    if (open) {
      el.style.transition = 'none';
      el.style.height = '0px';
      void el.getBoundingClientRect();
      openRafRef.current = window.requestAnimationFrame(() => {
        const current = ref.current;
        if (!current) return;
        current.style.transition =
          'height 380ms cubic-bezier(0.22, 1, 0.36, 1)';
        current.style.height = measuredHeight;
      });
    } else {
      el.style.transition = 'none';
      el.style.height =
        el.style.height && el.style.height !== 'auto'
          ? el.style.height
          : measuredHeight;
      void el.getBoundingClientRect();
      closeRafRef.current = window.requestAnimationFrame(() => {
        const current = ref.current;
        if (!current) return;
        current.style.transition =
          'height 320ms cubic-bezier(0.22, 1, 0.36, 1)';
        current.style.height = '0px';
      });
      closeTimeoutRef.current = window.setTimeout(() => {
        setShouldRender(false);
      }, 340);
    }

    el.addEventListener('transitionend', onEnd);

    return () => {
      clearScheduledWork();
      el.removeEventListener('transitionend', onEnd);
    };
  }, [open, shouldRender]);

  if (!shouldRender) {
    return null;
  }

  return (
    <div
      ref={(node) => {
        ref.current = node;
        assignRef(outerRef, node);
      }}
      className={`${styles.Table__Payments__Collapsible} ${className || ''}`.trim()}
    >
      <div ref={innerRef}>{children}</div>
    </div>
  );
}
